<i18n>
{
  "fr": {
    "login-title": "Invitation au portail",
    "login-desc": "Veuillez saisir un nouveau mot de passe pour votre compte",
    "send": "Envoyer",
    "password-label": "Votre mot de passe",
    "confirm-password-label": "Confirmer votre mot de passe",
    "required-field": "Ce champs est requis",
    "min-length-requirement": "Le mot de passe doit faire au minimum 8 caractères",
    "same-password-requirement": "Les mots de passes ne sont pas identiques",
    "verify-desc": "Vérification des informations...",
    "wrong-hash-desc": "Ce lien n'existe pas ou n'est plus valable.",
    "goto-ask-new-password": "Demander une nouvelle invitation",
    "cant-process": "Impossible de traiter la demande pour le moment. Veuillez réessayer plus tard.",
    "not-enough-access": "Le mot de passe a bien été changé. Cependant, vos droits d'accès sont insufisant pour accéder au portail. Veuillez contacter le CREALP pour plus d'information."
  },
  "de": {

  }
}
</i18n>

<template>
  <div id="login">
    <div v-if="ie" class="error">
      <p><b>Microsoft Internet Explorer</b></p>
      <p>Vous utilisez Microsoft Internet Explorer pour consulter ce site mais <b>celui-ci n’est pas capable d’afficher correctement son contenu</b>.<br />Veuillez mettre à jour votre navigateur.</p>
    </div>

    <div class="login-form-container">

      <md-card class="connection-form">
        <md-card-header>
          <div class="md-title">{{ $t('login-title') }} <md-icon :class="keyColorClass">vpn_key</md-icon></div>
        </md-card-header>

        <md-card-content v-if="loading">
          <p class="login-desc">{{ $t('verify-desc') }}</p>
        </md-card-content>

        <md-card-content v-if="!loading && wrongHashError">
          <p class="login-desc">{{ $t('wrong-hash-desc') }}</p>
        </md-card-content>

        <md-card-content v-if="!loading && !wrongHashError">
          <p class="login-desc">{{ $t('login-desc') }}</p>

          <form v-on:keyup.enter="send">
            <md-field :class="getValidationClass('password')">
              <label>{{ $t('password-label') }}</label>
              <md-input ref="passwordInput" :disabled="processing" v-model="password" type="password" autocomplete="new-password"></md-input>
              <span class="md-error" v-if="!$v.password.required">{{ $t('required-field') }}</span>
              <span class="md-error" v-if="!$v.password.minLength">{{ $t('min-length-requirement') }}</span>
            </md-field>
            <md-field :class="getValidationClass('passwordConfirm')">
              <label>{{ $t('confirm-password-label') }}</label>
              <md-input :disabled="processing" v-model="passwordConfirm" type="password"></md-input>
              <span class="md-error" v-if="!$v.passwordConfirm.required">{{ $t('required-field') }}</span>
              <span class="md-error" v-if="!$v.passwordConfirm.sameAsPassword">{{ $t('same-password-requirement') }}</span>
            </md-field>
          </form>

          <span class="fail-text" v-if="!!fail">{{ fail }}</span>

        </md-card-content>

        <md-card-actions>
          <md-button class="md-raised md-primary" v-if="!loading && !wrongHashError" :disabled="processing" @click="send">{{ $t('send') }}</md-button>
          <md-menu md-direction="bottom-end">
            <md-button md-menu-trigger>{{$t($root.$i18n.locale)}}</md-button>

            <md-menu-content class="md-layout-item md-size-10">
              <md-menu-item @click="$root.$i18n.locale = 'fr'">{{ $t('fr') }}</md-menu-item>
              <md-menu-item @click="$root.$i18n.locale = 'de'">{{ $t('de') }}</md-menu-item>
            </md-menu-content>
          </md-menu>
        </md-card-actions>

      </md-card>

    </div>

    <Snackbar ref="snackbar" />

  </div>
</template>

<script>

import UAParser from 'ua-parser-js'
import { validationMixin } from 'vuelidate'
import {
  required,
  sameAs,
  minLength
} from 'vuelidate/lib/validators'

import Auth from '@/utils/auth'
import AccessToken from '@/utils/accessToken'

import Snackbar from '@/components/common/snackbar'

const useragent = new UAParser()

export default {
  name: 'Login',
  components: {
    Snackbar
  },
  mixins: [validationMixin],
  validations: {
    password: {
      required,
      minLength: minLength(8)
    },
    passwordConfirm: {
      required,
      sameAsPassword: sameAs('password')
    }
  },
  data () {
    return {
      loading: true,
      wrongHashError: false,
      password: '',
      passwordConfirm: '',
      processing: false,
      fail: '',
      user: null
    }
  },
  computed: {
    browser () {
      return useragent.getBrowser()
    },
    ie () {
      return this.browser.name === 'IE'
    },
    edge () {
      return this.browser.name === 'Edge' && parseInt(this.browser.major, 10) < 76
    },
    keyColorClass () {
      if (!this.processing && !this.fail) return 'md-primary'
      if (this.fail !== '') return 'md-accent'
      return ''
    },
    hashCode () {
      return this.$route.params.hashCode
    },
    redirectTo () {
      return typeof this.$route.query.redirectTo !== 'undefined' ? this.$route.query.redirectTo : 'home'
    }
  },
  mounted () {
    this.wrongHashError = false
    this.loading = true

    Auth.checkInvitation(this.hashCode).then((user) => {
      this.user = user
      this.loading = false
      this.focusLoginField()
    }).catch((error) => {
      this.loading = false
      if (error.response.status === 422) this.wrongHashError = true
      else this.handleError(error.toString())
    })
  },
  methods: {
    getValidationClass (fieldName) {
      const field = this.$v[fieldName]

      if (field) {
        return {
          'md-invalid': field.$invalid && field.$dirty
        }
      }
    },
    isFormValid () {
      this.$v.$touch()
      return !this.$v.$invalid
    },
    focusLoginField () {
      setTimeout(() => {
        this.$refs.passwordInput.$el.focus()
      }, 100)
    },
    send () {
      if (this.isFormValid()) {
        this.processing = true
        this.fail = ''
        Auth.invitation(this.user.id, this.hashCode, this.password).then((token) => {
          AccessToken.set(token)
          const accessToken = AccessToken.get()
          // Contrôle des droits d'accès
          if (accessToken.hasRight('strates_viewer')) {
            this.$router.push({ name: this.redirectTo, query: this.$route.query })
          } else {
            this.fail = this.$t('not-enough-access')
            this.focusLoginField()
          }
        }).catch(() => {
          this.processing = false
          this.fail = this.$t('cant-process')
        })
      }
    },
    handleError (e) {
      this.$refs.snackbar.showError(e)
    }
  }
}
</script>

<style scoped lang="scss">
  .login-desc {
    margin-top: 0;
  }

  .error {
    background-color: #ff9191
  }

  .fail-text {
    padding-top: 15px;
    color: #ff1744;
    display: block;
  }

  .login-form-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: calc(100vh - 120px);
  }

  .connection-form {
    max-width: 500px;
  }

  .help-go-form {
    padding: 15px;
  }
</style>
